function parseMoneyToken(token: string): number {
  const normalized = token.replace(/^\$/, "").replace(/,/g, "").trim();
  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
}

function parseQty(token: string): number {
  const value = Number(token.replace(/,/g, ""));
  return Number.isFinite(value) ? value : 0;
}

function normalizePdfLine(line: string): string {
  return line.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

const PATTERN_A = /^(.+?)\s-\s+(\S+)\s+([\d,.]+)\s*(\$[\d,.]+)\s*(\$[\d,.]+)\s*$/i;
const PATTERN_B = /^(.+?)\s+([\d,.]+)\s*(\$[\d,.]+)\s*(\$[\d,.]+)\s*$/i;
/** HoneyBook sub-lines with unit + total only (no qty column), e.g. "Coffee & tea service $150 $150". */
const PATTERN_TWO_MONEY = /^(.+?)\s+(\$[\d,.]+)\s+(\$[\d,.]+)\s*$/i;
const TAIL_QTY_TWO_MONEY = /\s+([\d,.]+)\s*(\$\s*[\d,.]+)\s*(\$\s*[\d,.]+)\s*$/i;
const TAIL_QTY_ONE_MONEY = /\s+([\d,.]+)\s*(\$\s*[\d,.]+)\s*$/i;

function isProbablyInvoiceTableHeaderLine(line: string): boolean {
  const value = line.trim().toLowerCase();
  if (!value || value.length > 140) return false;
  const hasQty = /\bqty\b|\bquantity\b/.test(value);
  if (!hasQty) return false;
  return /\bunit\b/.test(value) && /\bprice\b|\btotal\b/.test(value);
}

function tryParseStructuredLine(line: string): {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit?: string;
} | null {
  const trimmed = line.trim();
  if (!trimmed.includes("$")) return null;

  const tailTwo = trimmed.match(TAIL_QTY_TWO_MONEY);
  if (tailTwo && tailTwo.index !== undefined && tailTwo.index > 0) {
    return {
      name: trimmed.slice(0, tailTwo.index).trim(),
      quantity: parseQty(tailTwo[1]!),
      unitPrice: parseMoneyToken(tailTwo[2]!),
      totalPrice: parseMoneyToken(tailTwo[3]!),
    };
  }

  const matchA = trimmed.match(PATTERN_A);
  if (matchA) {
    return {
      name: matchA[1]!.trim(),
      unit: matchA[2]!.trim(),
      quantity: parseQty(matchA[3]!),
      unitPrice: parseMoneyToken(matchA[4]!),
      totalPrice: parseMoneyToken(matchA[5]!),
    };
  }

  const matchB = trimmed.match(PATTERN_B);
  if (matchB) {
    return {
      name: matchB[1]!.trim(),
      quantity: parseQty(matchB[2]!),
      unitPrice: parseMoneyToken(matchB[3]!),
      totalPrice: parseMoneyToken(matchB[4]!),
    };
  }

  const matchTwoMoney = trimmed.match(PATTERN_TWO_MONEY);
  if (matchTwoMoney) {
    const unitPrice = parseMoneyToken(matchTwoMoney[2]!);
    const totalPrice = parseMoneyToken(matchTwoMoney[3]!);
    if (unitPrice > 0 && totalPrice > 0) {
      return {
        name: matchTwoMoney[1]!.trim(),
        quantity: 1,
        unitPrice,
        totalPrice,
      };
    }
  }

  const tailOne = trimmed.match(TAIL_QTY_ONE_MONEY);
  if (tailOne && tailOne.index !== undefined && tailOne.index > 0) {
    const price = parseMoneyToken(tailOne[2]!);
    return {
      name: trimmed.slice(0, tailOne.index).trim(),
      quantity: parseQty(tailOne[1]!),
      unitPrice: price,
      totalPrice: price,
    };
  }

  return null;
}

export function parseInvoiceLines(rawLines: string[]): Array<{
  rawLine: string;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit?: string;
  rawNotes?: string;
}> {
  const items: Array<{
    rawLine: string;
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    unit?: string;
    rawNotes?: string;
  }> = [];
  const pendingNotes: string[] = [];

  for (const raw of rawLines) {
    const line = normalizePdfLine(raw);
    if (!line || isProbablyInvoiceTableHeaderLine(line)) continue;

    const parsed = tryParseStructuredLine(line);
    if (parsed && parsed.name && parsed.quantity > 0) {
      if (items.length > 0 && pendingNotes.length > 0) {
        items[items.length - 1]!.rawNotes = pendingNotes.join("\n");
        pendingNotes.length = 0;
      }
      items.push({ rawLine: line, ...parsed });
      continue;
    }

    if (items.length > 0) pendingNotes.push(line);
  }

  if (items.length > 0 && pendingNotes.length > 0) {
    items[items.length - 1]!.rawNotes = pendingNotes.join("\n");
  }

  return items;
}
